<script setup lang="ts">
import { onBeforeUnmount, onMounted, proxyRefs, useTemplateRef } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import KioskStepProgressIndicator from '@/app/layouts/kiosk/KioskStepProgressIndicator.vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import UploadedPhotosSidebar from '@/components/UploadedPhotosSidebar.vue';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
// biome-ignore lint/style/useImportType: component is rendered in <template>
import PhotoFromPhoneFooter from './photo-from-phone/PhotoFromPhoneFooter.vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import PhotoFromPhoneInstructions from './photo-from-phone/PhotoFromPhoneInstructions.vue';
// biome-ignore lint/style/useImportType: component is rendered in <template>
import PhotoFromPhoneQrPanel from './photo-from-phone/PhotoFromPhoneQrPanel.vue';
import {
  type PhotoFromPhoneConsents,
  type PhotoFromPhoneFlow,
  usePhotoFromPhoneFlow,
} from './photo-from-phone/usePhotoFromPhoneFlow';

type PulseAnimation =
  PhotoFromPhoneFlow['continueButtonPulseAnimation']['value'];

interface PhotoFromPhonePublicApi {
  _isUnmounted: boolean;
  _mountSessionId: string | null | undefined;
  _pollingStartTimeout: ReturnType<typeof setTimeout> | null;
  readonly allGdprConsentsChecked: boolean;
  continueButtonPulseAnimation: PulseAnimation;
  readonly disableSkipButton: boolean;
  gdprConsents: PhotoFromPhoneConsents;
  generateQRCode: (link: string) => Promise<string>;
  getPhotoUploadLink: () => string;
  hasUploadedAndAnimatedPhotos: boolean;
  hasUploadedPhotos: boolean;
  readonly hasWebcamPhotos: boolean;
  onCancelUploadClick: () => void;
  onConfirmClick: () => void;
  onPhotoUpload: () => void;
  onPhotoUploadAndAnimationComplete: () => void;
  onPrivacyPolicyClick: () => void;
  onSkipKioskPhotoClick: () => void;
  qrCodeUrl: string | null;
  readonly showBackToWebcamButton: boolean;
  readonly showGdprCheckboxes: boolean;
}

const qrPanel =
  useTemplateRef<InstanceType<typeof PhotoFromPhoneQrPanel>>('qrPanel');
const footer =
  useTemplateRef<InstanceType<typeof PhotoFromPhoneFooter>>('footer');

const flow = usePhotoFromPhoneFlow({
  getContinueButton: (): HTMLElement | null =>
    footer.value?.getContinueButton() ?? null,
  getQrCodeContainer: (): HTMLElement | null =>
    qrPanel.value?.getQrCodeContainer() ?? null,
});

const {
  allGdprConsentsChecked,
  continueButtonPulseAnimation,
  disableSkipButton,
  enter,
  gdprConsents,
  generateQRCode,
  getPhotoUploadLink,
  hasUploadedAndAnimatedPhotos,
  hasUploadedPhotos,
  hasWebcamPhotos,
  isUnmounted,
  leave,
  mountSessionId,
  onCancelUploadClick,
  onConfirmClick,
  onPhotoUpload,
  onPhotoUploadAndAnimationComplete,
  onPrivacyPolicyClick,
  onSkipKioskPhotoClick,
  pollingStartTimeout,
  qrCodeUrl,
  showBackToWebcamButton,
  showGdprCheckboxes,
}: PhotoFromPhoneFlow = flow;

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onGdprConsentChange(
  key: keyof PhotoFromPhoneConsents,
  value: boolean
): void {
  gdprConsents[key] = value;
}

const publicApi: PhotoFromPhonePublicApi = proxyRefs({
  _isUnmounted: isUnmounted,
  _mountSessionId: mountSessionId,
  _pollingStartTimeout: pollingStartTimeout,
  get allGdprConsentsChecked(): boolean {
    return allGdprConsentsChecked.value;
  },
  continueButtonPulseAnimation,
  get disableSkipButton(): boolean {
    return disableSkipButton.value;
  },
  get gdprConsents(): PhotoFromPhoneConsents {
    return gdprConsents;
  },
  set gdprConsents(value: PhotoFromPhoneConsents) {
    Object.assign(gdprConsents, value);
  },
  generateQRCode,
  getPhotoUploadLink,
  hasUploadedAndAnimatedPhotos,
  hasUploadedPhotos,
  get hasWebcamPhotos(): boolean {
    return hasWebcamPhotos.value;
  },
  onCancelUploadClick,
  onConfirmClick,
  onPhotoUpload,
  onPhotoUploadAndAnimationComplete,
  onPrivacyPolicyClick,
  onSkipKioskPhotoClick,
  qrCodeUrl,
  get showBackToWebcamButton(): boolean {
    return showBackToWebcamButton.value;
  },
  get showGdprCheckboxes(): boolean {
    return showGdprCheckboxes.value;
  },
});

onMounted((): void => {
  exposeToGlobalScope(publicApi, 'PhotoFromPhone');
  enter();
});

onBeforeUnmount(leave);

defineExpose(publicApi);
</script>

<template>
  <KioskLayout
    :classes="{ container: 'PhotoFromPhoneScreen', content: 'PhotoFromPhone' }"
  >
    <template #KioskLayout__right>
      <UploadedPhotosSidebar
        :watch="true"
        @animation-complete="onPhotoUploadAndAnimationComplete"
      />
    </template>

    <template #KioskLayout__bottom>
      <KioskStepProgressIndicator :index="0" />
    </template>

    <div class="PhotoFromPhone__main" data-testid="photo-phone-main">
      <PhotoFromPhoneQrPanel
        ref="qrPanel"
        :qr-code-url="qrCodeUrl"
        :show-qr-code="!showGdprCheckboxes || allGdprConsentsChecked"
      />

      <PhotoFromPhoneInstructions
        :gdpr-consents="gdprConsents"
        :has-uploaded-and-animated-photos="hasUploadedAndAnimatedPhotos"
        :show-gdpr-checkboxes="showGdprCheckboxes"
        @gdpr-consent-change="onGdprConsentChange"
        @privacy-policy-click="onPrivacyPolicyClick"
      />
    </div>

    <PhotoFromPhoneFooter
      ref="footer"
      :all-gdpr-consents-checked="allGdprConsentsChecked"
      :disable-skip-button="disableSkipButton"
      :has-uploaded-and-animated-photos="hasUploadedAndAnimatedPhotos"
      :has-webcam-photos="hasWebcamPhotos"
      :show-back-to-webcam-button="showBackToWebcamButton"
      @cancel-upload-click="onCancelUploadClick"
      @confirm-click="onConfirmClick"
      @skip-kiosk-photo-click="onSkipKioskPhotoClick"
    />
  </KioskLayout>
</template>

<style scoped lang="scss">
.PhotoFromPhoneScreen {
  height: 100vh;
  height: 100dvh;
  grid-template-areas: 'stage' 'bottom';
  grid-template-rows: minmax(0, 1fr) auto;

  :deep(.KioskLayout__inner) {
    min-height: 0;
    min-width: 0;
  }

  :deep(.PhotoFromPhone) {
    display: grid;
    grid-template-rows: minmax(0, 1fr) auto;
    gap: 1rem;
    width: 100%;
    min-height: 0;
    min-width: 0;
    padding-bottom: 1rem;
  }

  :deep(.KioskLayout__bottom) {
    grid-area: bottom;
    justify-self: center;
    left: auto;
    transform: none;
  }
}

.PhotoFromPhone__main {
  display: flex;
  justify-self: center;
  flex-direction: var(--photo-from-phone-main-direction, row);
  align-items: var(--photo-from-phone-main-alignment, safe center);
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  min-height: 0;
  overflow: auto;

  > :deep(*) {
    flex-shrink: 0;
  }
}

</style>
